import React, { useState } from 'react';
import { CheckCircle2Icon, LockIcon, AlertCircleIcon, Loader2Icon, PhoneIcon, MailIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { Button } from '../components/Button';
import { Reveal } from '../components/Reveal';
import { liveCities, plannedCities } from '../data/cities';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormState {
  name: string;
  email: string;
  phone: string;
  city: string;
  assets: string;
  contactTime: string;
  notes: string;
}
const emptyForm: FormState = {
  name: '',
  email: '',
  phone: '',
  city: '',
  assets: '',
  contactTime: 'Weekday mornings',
  notes: ''
};
const assetBands = ['Prefer not to say', 'Under ₹5 crore', '₹5 – 15 crore', '₹15 – 50 crore', 'Above ₹50 crore'];
const contactTimes = ['Weekday mornings', 'Weekday afternoons', 'Weekday evenings', 'Saturday mornings'];
const fieldClass = 'mt-2 h-11 w-full rounded-sm border border-line bg-cream px-3.5 text-[0.95rem] text-charcoal transition-[border-color] duration-200 ease-out-quint placeholder:text-charcoal-400 hover:border-charcoal/25 focus:border-gold';
const labelClass = 'block text-sm font-medium text-charcoal';
export function Contact() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const update = (key: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: event.target.value
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.city || !form.email.trim() && !form.phone.trim()) {
      setStatus('error');
      setError('Please share your name, your city, and either a phone number or an email address.');
      return;
    }
    setError(null);
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <main data-id="element-268" data-mp="1h7qyh4eu9z73">
      <PageHeader eyebrow="Get started" title="Tell us a little, and we will call you back ourselves." lede="A first conversation is a conversation — not a pitch. In Bengaluru and Chennai you will speak to Prashanth directly; elsewhere, we will tell you honestly whether we can serve your family well from where we are today." data-id="element-269" data-mp="1h80sr46x2sil" />

      <Section data-id="element-270" data-mp="yike7cyeczj6">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-271" data-mp="yj3telwnx9ti">
          <div className="lg:col-span-7" data-id="element-272" data-mp="1d7724323azm0">
            {status === 'success' ? <Reveal className="rounded-sm border border-gold/40 bg-paper p-8" data-id="element-273" data-mp="18fyunckblobh">
                <CheckCircle2Icon className="h-7 w-7 text-gold-dark" aria-hidden="true" data-id="element-274" data-mp="18dvvo316wkum" />
                <h2 className="mt-4 font-serif text-h3 text-charcoal" data-id="element-275" data-mp="kojjcuakfwo3">
                  Thank you, {form.name.split(' ')[0]}.
                </h2>
                <p className="mt-3 max-w-md text-[0.95rem] leading-relaxed text-charcoal-600" data-id="element-276" data-mp="2re4w6okb4sc4">
                  Your enquiry has reached our Bengaluru desk, flagged for {form.city}. Expect a
                  call within one working day, during {form.contactTime.toLowerCase()}.
                </p>
                <Button variant="outline" className="mt-6" onClick={() => {
              setForm(emptyForm);
              setStatus('idle');
            }} data-id="element-277" data-mp="2mf0hjiem62ee">
                  Send another enquiry
                </Button>
              </Reveal> : <form onSubmit={handleSubmit} noValidate data-id="element-278" data-mp="316ix5f3k9ux8">
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-279" data-mp="3559u9m811ckc">
                  <div className="sm:col-span-2" data-id="element-280" data-mp="1ppasqtsx0et2">
                    <label className={labelClass} htmlFor="name" data-id="element-281" data-mp="1pohvcjfuj1nl">
                      Your name
                    </label>
                    <input id="name" className={fieldClass} value={form.name} onChange={update('name')} placeholder="Ramesh Agrawal" autoComplete="name" data-id="element-282" data-mp="1ts7e2s9uap0p" />
                  </div>

                  <div data-id="element-283" data-mp="1v9ehg70okpzi">
                    <label className={labelClass} htmlFor="phone" data-id="element-284" data-mp="1v9wcj0blwy5u">
                      Phone
                    </label>
                    <input id="phone" type="tel" className={fieldClass} value={form.phone} onChange={update('phone')} placeholder="+91 98765 43210" autoComplete="tel" data-id="element-285" data-mp="2swd5jtdb4pls" />
                  </div>

                  <div data-id="element-286" data-mp="17k21moauljo8">
                    <label className={labelClass} htmlFor="email" data-id="element-287" data-mp="17kbpa58f7eqr">
                      Email
                    </label>
                    <input id="email" type="email" className={fieldClass} value={form.email} onChange={update('email')} placeholder="you@example.com" autoComplete="email" data-id="element-288" data-mp="13gnktvf34z9q" />
                  </div>

                  <div data-id="element-289" data-mp="3sxgycfgpk40n">
                    <label className={labelClass} htmlFor="city" data-id="element-290" data-mp="3sy05r28qfa0f">
                      City
                    </label>
                    <select id="city" className={fieldClass} value={form.city} onChange={update('city')} data-id="element-291" data-mp="1sqoq02pu62y">
                      <option value="" data-id="element-292" data-mp="ar2jmeam7q17">Select your city</option>
                      <optgroup label="Offices open now" data-id="element-293" data-mp="arviyu78vd9b">
                        {liveCities.map(city => <option key={city.name} value={city.name} data-id="element-294" data-mp="fo2pop7gsssb">
                            {city.name}
                          </option>)}
                      </optgroup>
                      <optgroup label="Opening soon — we travel meanwhile" data-id="element-295" data-mp="2hjmnccwqzmrs">
                        {plannedCities.map(city => <option key={city.name} value={city.name} data-id="element-296" data-mp="3ftw6fn2lj1rq">
                            {city.name}
                          </option>)}
                      </optgroup>
                      <option value="Another city" data-id="element-297" data-mp="3otjakgmpmloh">Another city</option>
                    </select>
                  </div>

                  <div data-id="element-298" data-mp="7kmjq2d15dh0">
                    <label className={labelClass} htmlFor="assets" data-id="element-299" data-mp="7k2yabo686y8">
                      Approximate investable assets{' '}
                      <span className="font-normal text-charcoal-400" data-id="element-300" data-mp="ge7xbidgkoou">(optional)</span>
                    </label>
                    <select id="assets" className={fieldClass} value={form.assets} onChange={update('assets')} data-id="element-301" data-mp="cc49du9lrrmo">
                      <option value="" data-id="element-302" data-mp="qbm4sshem350">Prefer not to say</option>
                      {assetBands.slice(1).map(band => <option key={band} value={band} data-id="element-303" data-mp="levoq55ln7jl">
                          {band}
                        </option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2" data-id="element-304" data-mp="19xozyykf232o">
                    <label className={labelClass} htmlFor="contactTime" data-id="element-305" data-mp="v16n78wo3b1g">
                      Preferred contact time
                    </label>
                    <select id="contactTime" className={fieldClass} value={form.contactTime} onChange={update('contactTime')} data-id="element-306" data-mp="zyg9wgso98ll">
                      {contactTimes.map(time => <option key={time} value={time} data-id="element-307" data-mp="cbrnbhspwryn">
                          {time}
                        </option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2" data-id="element-308" data-mp="3b1w6reejpr9">
                    <label className={labelClass} htmlFor="notes" data-id="element-309" data-mp="7bcb4tk69qi9">
                      Anything you would like us to know{' '}
                      <span className="font-normal text-charcoal-400" data-id="element-310" data-mp="nr4rtbxjjv19">(optional)</span>
                    </label>
                    <textarea id="notes" rows={4} className={`${fieldClass} h-auto py-3 leading-relaxed`} value={form.notes} onChange={update('notes')} placeholder="A sale of the business, a succession question, scattered holdings across family members…" data-id="element-311" data-mp="it37knodppo7" />
                  </div>
                </div>

                {status === 'error' && error ? <p role="alert" className="mt-6 flex items-start gap-2 rounded-sm border border-gold/50 bg-paper px-4 py-3 text-sm text-charcoal-800" data-id="element-312" data-mp="3r8d65lt28cr8">
                    <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" data-id="element-313" data-mp="3h8m9pekxvnoi" />
                    {error}
                  </p> : null}

                <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-314" data-mp="1sozzrd4u36d1">
                  <Button type="submit" size="lg" disabled={status === 'submitting'} data-id="element-315" data-mp="1wqtt10cp9836">
                    {status === 'submitting' ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-316" data-mp="21on2c29aui7o" />
                        Sending
                      </> : 'Book a consultation'}
                  </Button>
                  <p className="text-sm text-charcoal-400" data-id="element-317" data-mp="1e1ew9i41r54l">No obligation, and nothing to sign.</p>
                </div>
              </form>}
          </div>

          <aside className="lg:col-span-5" data-id="element-318" data-mp="1nr6q2a4bnu80">
            <div className="rounded-sm border border-line bg-paper p-7" data-id="element-319" data-mp="1nrztp27zi3lx">
              <LockIcon className="h-5 w-5 text-gold-dark" aria-hidden="true" data-id="element-320" data-mp="qahuuz9vnq7l" />
              <h2 className="mt-4 font-serif text-h3 text-charcoal" data-id="element-321" data-mp="v9m39noz9jwv">
                What you share stays confidential
              </h2>
              <ul className="mt-5 space-y-3 text-[0.925rem] leading-relaxed text-charcoal-600" data-id="element-322" data-mp="oxcavo20lrmz">
                <li className="border-b border-line pb-3" data-id="element-323" data-mp="tv44qnm7zntt">
                  Your enquiry goes straight to Prashanth and the adviser for your city. Nobody else
                  in the firm sees it.
                </li>
                <li className="border-b border-line pb-3" data-id="element-324" data-mp="13ss63webztky">
                  We never share, sell or broker your details to a manufacturer, distributor or
                  third-party platform.
                </li>
                <li className="border-b border-line pb-3" data-id="element-325" data-mp="g5adxzr5mthg">
                  You are not added to a marketing list. If we are not a fit, we will say so and stop
                  contacting you.
                </li>
                <li data-id="element-326" data-mp="3jmisa9mobb7f">
                  Asset figures are optional and only help us route you to the right practice.
                </li>
              </ul>
            </div>

            <div className="mt-6 rounded-sm border border-line bg-cream p-7" data-id="element-327" data-mp="3oir3jbxr2k9b">
              <h2 className="font-serif text-h3 text-charcoal" data-id="element-328" data-mp="25p6bfzajc9vv">Prefer to call?</h2>
              <p className="mt-3 text-[0.925rem] leading-relaxed text-charcoal-600" data-id="element-329" data-mp="21ovwhx4rm94v">
                Our client services desk is open Monday to Saturday, 9am to 7pm IST.
              </p>
              <div className="mt-5 space-y-2.5 text-sm" data-id="element-330" data-mp="1rperzj1921ah">
                <a href="tel:+918012345678" className="flex items-center gap-2 font-medium text-charcoal transition-colors duration-200 ease-out-quint hover:text-gold-dark" data-id="element-331" data-mp="1mr3kvg2ca4vy">
                  <PhoneIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-332" data-mp="1dy05h284vhyo" />
                  +91 80 1234 5678
                </a>
                <a href="mailto:contact@merabucket.com" className="flex items-center gap-2 font-medium text-charcoal transition-colors duration-200 ease-out-quint hover:text-gold-dark" data-id="element-333" data-mp="191jlmk7yvpre">
                  <MailIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-334" data-mp="pe6kl2rv1dcj" />
                  contact@merabucket.com
                </a>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </main>;
}